import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import { useTier } from "../utils/tierContext";
import Toast from "./Toast";
import styles from "./TypeManager.module.css";

// ── Types ──────────────────────────────────────────────

interface TypeManagerProps {
  title: string;
  subtitle: string;
  apiPath: string;
  nameField: string;
  idField: string;
  feature: string;
  minLevel?: string;
}

interface TypeManagerContentProps extends TypeManagerProps {
  readOnly?: boolean;
}

interface TypeItem {
  [key: string]: any;
}

// ── Content component ──────────────────────────────────

const TypeManagerContent: React.FC<TypeManagerContentProps> = ({
  title,
  subtitle,
  apiPath,
  nameField,
  idField,
  readOnly = false,
}) => {
  const { token, getToken } = useAuthToken();
  const { user, activeTenantId } = useUser();

  const api = useMemo(() => {
    if (!token || !activeTenantId || !user?.userId) return null;
    return createApiClient({
      baseUrl: protectedResources.baseURL.endpoint,
      token,
      getToken,
      tenantId: activeTenantId,
      userId: user?.userId,
    });
  }, [token, getToken, activeTenantId, user?.userId]);

  const [items, setItems] = useState<TypeItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Add-new form state
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState("");
  const [addOrder, setAddOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editId, setEditId] = useState<string | number | null>(null);
  const [editName, setEditName] = useState("");
  const [editOrder, setEditOrder] = useState(0);

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | number | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const getId = useCallback(
    (item: TypeItem): string | number => item[idField] ?? item.id,
    [idField],
  );

  const isSystemDefault = (item: TypeItem): boolean => item.isSystem === true;
  const isActive = (item: TypeItem): boolean => item.isActive !== false;

  // Filter out inactive non-system items (soft-deleted custom types)
  // Sort: active before disabled, then custom (non-system) before system
  const sortedItems = useMemo(() => {
    return [...items]
      .filter((item) => isActive(item) || isSystemDefault(item))
      .sort((a, b) => {
        const aActive = isActive(a) ? 0 : 1;
        const bActive = isActive(b) ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        const aSystem = a.isSystem ? 1 : 0;
        const bSystem = b.isSystem ? 1 : 0;
        if (aSystem !== bSystem) return aSystem - bSystem;
        return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }, [items]);

  // ── Fetch ────────────────────────────────────────────

  const fetchItems = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const data = await api.get(`${apiPath}?includeInactive=true`);
      const list = Array.isArray(data) ? data : [];
      setItems(list);
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to load items.", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [api, apiPath]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Add ──────────────────────────────────────────────

  const handleAdd = async () => {
    if (!api || !addName.trim()) return;
    setSaving(true);
    try {
      await api.post(`${apiPath}`, { [nameField]: addName.trim(), sortOrder: addOrder });
      setAddName("");
      setAddOrder(0);
      setShowAdd(false);
      await fetchItems();
      setToast({ message: "Item added.", type: "success" });
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to add item.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────

  const startEdit = (item: TypeItem) => {
    setEditId(getId(item));
    setEditName(item[nameField] ?? "");
    setEditOrder(item.sortOrder ?? 0);
    setConfirmDeleteId(null);
  };

  const cancelEdit = () => {
    setEditId(null);
  };

  const handleEdit = async () => {
    if (!api || editId === null || !editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`${apiPath}/${editId}`, {
        [nameField]: editName.trim(),
        sortOrder: editOrder,
      });
      setEditId(null);
      await fetchItems();
      setToast({ message: "Item updated.", type: "success" });
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to update item.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle active/disabled ──────────────────────────

  const handleToggleActive = async (item: TypeItem) => {
    if (!api) return;
    const id = getId(item);
    setSaving(true);
    try {
      const wasActive = isActive(item);
      await api.put(`${apiPath}/${id}/toggle`, {
        isActive: !wasActive,
      });
      await fetchItems();
      setToast({ message: wasActive ? "Item disabled." : "Item enabled.", type: "success" });
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to update status.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────

  const handleDelete = async (id: string | number) => {
    if (!api) return;
    setSaving(true);
    try {
      await api.delete(`${apiPath}/${id}`);
      setConfirmDeleteId(null);
      await fetchItems();
      setToast({ message: "Item deleted.", type: "success" });
    } catch (err: any) {
      setToast({ message: err?.message || "Failed to delete item.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────

  return (
    <>
      <div className={styles["tm-card"]}>
        <div className={styles["tm-card-header"]}>
          <h2 className={styles["tm-card-header-title"]}>{title}</h2>
          {!readOnly && (
            <button
              className={styles["tm-btn-add"]}
              onClick={() => {
                setShowAdd(true);
                setAddName("");
                setAddOrder(items.length > 0 ? Math.max(...items.map((i) => i.sortOrder ?? 0)) + 1 : 1);
              }}
            >
              + Add New
            </button>
          )}
        </div>
        <div className={styles["tm-card-body"]}>
          {loading && (
            <div className={styles["tm-table-wrap"]}>
              <table className={styles["tm-table"]}>
                <thead>
                  <tr>
                    <th>Name</th>
                    {!readOnly && <th className={styles["tm-col-actions"]}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].map((i) => (
                    <tr key={i} style={{ opacity: 0.5 }}>
                      <td><span className={styles["tm-skeleton"]} style={{ width: 140, height: 13 }} /></td>
                      {!readOnly && <td><span className={styles["tm-skeleton"]} style={{ width: 60, height: 13 }} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && (
            <div className={styles["tm-table-wrap"]}>
              <table className={styles["tm-table"]}>
            <thead>
              <tr>
                <th>Name</th>
                {!readOnly && <th className={styles["tm-col-actions"]}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {!readOnly && showAdd && (
                <tr>
                  <td colSpan={2}>
                    <div className={styles["tm-inline-form"]}>
                      <input
                        type="text"
                        className={styles["tm-input"]}
                        placeholder="Name"
                        aria-label="New type name"
                        value={addName}
                        onChange={(e) => setAddName(e.target.value)}
                        data-testid="type-add-input"
                        autoFocus
                      />
                      <button
                        className={styles["tm-btn"]}
                        disabled={saving || !addName.trim()}
                        onClick={handleAdd}
                        data-testid="type-add-save"
                      >
                        Save
                      </button>
                      <button
                        className={styles["tm-btn-secondary"]}
                        onClick={() => setShowAdd(false)}
                        disabled={saving}
                        data-testid="type-add-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {sortedItems.map((item) => {
                const id = getId(item);
                const sysDefault = isSystemDefault(item);
                const active = isActive(item);
                const isEditing = !readOnly && editId === id;
                const isConfirmingDelete = !readOnly && confirmDeleteId === id;

                if (isEditing) {
                  return (
                    <tr key={id} data-testid={`type-row-${id}`}>
                      <td colSpan={2}>
                        <div className={styles["tm-inline-form"]}>
                          <input
                            type="text"
                            className={styles["tm-input"]}
                            placeholder="Name"
                            aria-label="Edit type name"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            data-testid={`type-edit-input-${editId}`}
                            autoFocus
                          />
                          <button
                            className={styles["tm-btn"]}
                            disabled={saving || !editName.trim()}
                            onClick={handleEdit}
                            data-testid={`type-edit-save-${id}`}
                          >
                            Save
                          </button>
                          <button
                            className={styles["tm-btn-secondary"]}
                            onClick={cancelEdit}
                            disabled={saving}
                            data-testid={`type-edit-cancel-${id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={id} className={!active ? styles["tm-row-disabled"] : undefined} data-testid={`type-row-${id}`}>
                    <td>
                      <span className={styles["tm-name"]}>
                        {item[nameField]}
                      </span>
                      {sysDefault && (
                        <span className={styles["tm-badge-system"]}>System</span>
                      )}
                      <span className={active ? styles["tm-badge-active"] : styles["tm-badge-inactive"]}>
                        {active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    {!readOnly && (
                    <td className={styles["tm-col-actions"]}>
                      {isConfirmingDelete ? (
                        <div className={styles["tm-confirm"]}>
                          <span className={styles["tm-confirm-text"]}>Delete?</span>
                          <button
                            className={styles["tm-btn-danger"]}
                            disabled={saving}
                            onClick={() => handleDelete(id)}
                            data-testid={`type-delete-yes-${id}`}
                          >
                            Yes
                          </button>
                          <button
                            className={styles["tm-btn-secondary"]}
                            disabled={saving}
                            onClick={() => setConfirmDeleteId(null)}
                            data-testid={`type-delete-no-${id}`}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className={styles["tm-actions"]}>
                          {sysDefault && (
                            <button
                              className={styles["tm-toggle-btn"]}
                              onClick={() => handleToggleActive(item)}
                              disabled={saving}
                              title={active ? "Disable" : "Enable"}
                              aria-label={`${active ? "Disable" : "Enable"} ${item[nameField]}`}
                            >
                              {active ? "Disable" : "Enable"}
                            </button>
                          )}
                          {!sysDefault && (
                            <>
                              <button
                                className={styles["tm-icon-btn"]}
                                onClick={() => startEdit(item)}
                                disabled={saving}
                                aria-label={`Edit ${item[nameField]}`}
                                title="Edit"
                              >
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M13.586 3.586a2 2 0 1 1 2.828 2.828L7.5 15.328l-3.75 1.172 1.172-3.75 8.664-8.664Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                              <button
                                className={`${styles["tm-icon-btn"]} ${styles["tm-icon-btn--danger"]}`}
                                onClick={() => {
                                  setConfirmDeleteId(id);
                                  setEditId(null);
                                }}
                                disabled={saving}
                                aria-label={`Delete ${item[nameField]}`}
                                title="Delete"
                              >
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M5.833 6.667v8.75A1.25 1.25 0 0 0 7.083 16.667h5.834a1.25 1.25 0 0 0 1.25-1.25v-8.75M4.167 5h11.666M7.5 5V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25V5M8.333 9.167v4.166M11.667 9.167v4.166" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    )}
                  </tr>
                );
              })}

              {!loading && items.length === 0 && !showAdd && (
                <tr>
                  <td colSpan={3}>
                    <div className={styles["tm-empty"]}>
                      <svg className={styles["tm-empty__icon"]} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="6" width="32" height="36" rx="4" stroke="#d1d5db" strokeWidth="1.5" />
                        <path d="M24 18v12M18 24h12" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      <div className={styles["tm-empty__title"]}>No Items Yet</div>
                      <div className={styles["tm-empty__subtitle"]}>Click &quot;Add New&quot; above to create one.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
            </div>
          )}
        </div>
      </div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </>
  );
};

// ── Wrapper with auth + read-only detection ───────────────────

const LEVEL_ORDER: Record<string, number> = { standard: 1, advanced: 2 };

const TypeManager: React.FC<TypeManagerProps> = (props) => {
  const authRequest = { ...loginRequest };
  const { hasFeature, getFeatureLevel } = useTier();

  const canEdit = (() => {
    if (!hasFeature(props.feature)) return false;
    if (props.minLevel) {
      const currentRank = LEVEL_ORDER[getFeatureLevel(props.feature) ?? ""] ?? 0;
      const requiredRank = LEVEL_ORDER[props.minLevel] ?? 0;
      if (currentRank < requiredRank) return false;
    }
    return true;
  })();

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
        <div className={styles["tm-shell"]}>
          <TypeManagerContent {...props} readOnly={!canEdit} />
        </div>
    </MsalAuthenticationTemplate>
  );
};

export default TypeManager;
